import { EndOrderDetailPaymentStatus, EndOrderDetails, Order } from '@pedix-workspace/utils';
import { coversOrderTotal, getOrderAmountDue } from './order-payment-totals';

export type OrderListItem = Order | EndOrderDetails;

export type OrderPaidState = Pick<
  EndOrderDetails,
  'paidAmount' | 'finalAmount' | 'digitalPayment' | 'marketplaceOrder'
> &
  Partial<Pick<EndOrderDetails, 'status'>>;

export function isOrder(order: Order | OrderPaidState): order is Order {
  return 'date' in order;
}

export function isEndOrderDetails(order: OrderListItem): order is EndOrderDetails {
  return 'created' in order;
}

export function isTableSessionOrder(order: OrderListItem): boolean {
  return Boolean(order.tableSessionId);
}

export function isMarketplaceOrder(order: Pick<EndOrderDetails, 'marketplaceOrder'>): boolean {
  return Boolean(order.marketplaceOrder);
}

export function isCollectedByMarketplace(
  order: Pick<EndOrderDetails, 'marketplaceOrder'> & Partial<Pick<EndOrderDetails, 'status'>>,
): boolean {
  return order.marketplaceOrder?.collectedBy === 'marketplace' && order.status !== 'canceled';
}

export function getOrderListItemName(order: OrderListItem): string {
  if (isOrder(order)) {
    return order.name || '';
  }
  return order.personalInformation?.name || '';
}

export function getOrderListItemPaymentMethod(order: OrderListItem): string {
  if (isOrder(order)) {
    return order.paymentMethod;
  }
  return order.paymentMethod?.name || '';
}

export function getOrderListItemShippingOption(order: OrderListItem): string {
  if (isOrder(order)) {
    return order.shippingOption || '';
  }
  return order.shippingOption?.name || '';
}

export function getOrderListItemDate(order: OrderListItem): Date {
  if (isOrder(order)) {
    return order.date;
  }
  return order.created;
}

export const UNPAID_PAYMENT_STATUS = 'UNPAID';

export function getOrderListItemAmountDue(order: Order | OrderPaidState): number | undefined {
  const finalAmount = isOrder(order) ? order.totalAmount : order.finalAmount;

  return typeof finalAmount === 'number'
    ? getOrderAmountDue({ finalAmount, marketplaceOrder: order.marketplaceOrder })
    : undefined;
}

export function getOrderPaymentDisplayStatus(
  order: Order | OrderPaidState,
): EndOrderDetailPaymentStatus | null {
  if (isCollectedByMarketplace(order)) {
    return 'PAID';
  }

  const amountDue = getOrderListItemAmountDue(order);

  if (
    typeof order.paidAmount === 'number' &&
    typeof amountDue === 'number' &&
    coversOrderTotal(order.paidAmount, amountDue)
  ) {
    return 'PAID';
  }
  if ((order.paidAmount ?? 0) > 0) {
    return 'PARTIAL';
  }

  const providerStatus = order.digitalPayment?.status ?? null;
  if (providerStatus === 'PAID' && typeof order.paidAmount === 'number') {
    return null;
  }
  return providerStatus;
}

export function isOrderPaid(order: Order | OrderPaidState): boolean {
  return getOrderPaymentDisplayStatus(order) === 'PAID';
}

export function isMarkPaidEligible(
  order: (Order | OrderPaidState) & Pick<EndOrderDetails, 'status'>,
): boolean {
  return !isOrderPaid(order) && order.status !== 'canceled';
}

export function getUncollectedOrders<T extends OrderPaidState & Pick<EndOrderDetails, 'status'>>(
  orders: T[],
): T[] {
  return orders.filter(order => order.status === 'completed' && isMarkPaidEligible(order));
}

export function isManualCollectionAvailable(
  order: (Order | OrderPaidState) & Pick<EndOrderDetails, 'status'>,
): boolean {
  const paymentStatus = getOrderPaymentDisplayStatus(order);

  return (
    order.status !== 'canceled' &&
    paymentStatus !== 'PAID' &&
    paymentStatus !== 'PENDING' &&
    paymentStatus !== 'PARTIAL'
  );
}
